import { formatDate, formatDateLong, formatShortDate } from "@/utils/formatDate";
import { formatCurrency } from "@/utils/formatEuro";
import {
  getListingPriceLabel,
  getTransactionNotAvailableLabel,
  isPriceOnApplication,
  shouldHideTransactionPrice,
} from "@/utils/transactionRecord";

import type { AuctionCarTransaction } from "@/types/auctions";
import type { TFunction } from "i18next";

export function normalizeTransactionSearchValue(value: unknown): string {
  return String(value ?? "")
    .toLowerCase()
    .replace(/[_-]/g, " ")
    .replace(/[,%+€$]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function getPriceDisplaySearchValues(
  row: AuctionCarTransaction,
  field: "hammer_price" | "listing_price" | "net_to_seller" | "price",
  t: TFunction,
  currencyCode: string
): string[] {
  const value = row[field];
  const values: string[] = [];

  if (field === "listing_price") {
    const listingPriceLabel = getListingPriceLabel(t, row, value);
    if (listingPriceLabel) {
      values.push(listingPriceLabel);
      return values;
    }
  }

  if (shouldHideTransactionPrice(row, value)) {
    values.push(getTransactionNotAvailableLabel(t));
    return values;
  }

  if (!isPriceOnApplication(value)) {
    values.push(formatCurrency(value, currencyCode), String(value));
  }

  return values;
}

function getTransactionDisplayedSearchValues(
  row: AuctionCarTransaction,
  t: TFunction,
  currencyCode: string
): string[] {
  const values: string[] = [];

  if (row.date) {
    values.push(formatDate(row.date), formatShortDate(row.date), formatDateLong(row.date));
  }

  values.push(
    ...getPriceDisplaySearchValues(row, "listing_price", t, currencyCode),
    ...getPriceDisplaySearchValues(row, "hammer_price", t, currencyCode),
    ...getPriceDisplaySearchValues(row, "net_to_seller", t, currencyCode),
    ...getPriceDisplaySearchValues(row, "price", t, currencyCode)
  );

  values.push(row.event_region?.trim() ? row.event_region : getTransactionNotAvailableLabel(t));
  values.push(row.event_country?.trim() ? row.event_country : getTransactionNotAvailableLabel(t));

  for (const field of [
    "vin",
    "seller",
    "auction_house",
    "event",
    "production_year",
    "original_currency",
    "type",
  ] as const) {
    const value = row[field];
    if (value !== undefined && value !== null && value !== "") {
      values.push(String(value));
    }
  }

  if (row.condition) {
    values.push(t(`car.conditions.${row.condition}`, row.condition));
    // Include raw value too (e.g. `not_available`)
    values.push(row.condition);
  }

  if (row.result) {
    values.push(t(`components.TransactionTableRow.result.${row.result}`, row.result));
    // Include raw value too (e.g. `not_available`)
    values.push(row.result);
  }

  if (row.status) {
    values.push(t(`car.auction_statuses.${row.status}`, row.status));
    // Include raw value too (e.g. `not_sold`)
    values.push(row.status);
  }

  if (row.outlier) {
    values.push(t("components.TransactionsTable.header.outlier", "Outlier"));
  }

  if (row.replica) {
    values.push(t("components.TransactionsTable.header.replica", "Replica"));
  }

  return values;
}

export function transactionMatchesSearch(
  row: AuctionCarTransaction,
  query: string,
  t: TFunction,
  currencyCode: string
): boolean {
  const q = normalizeTransactionSearchValue(query);
  if (!q) return true;

  return getTransactionDisplayedSearchValues(row, t, currencyCode).some((value) =>
    normalizeTransactionSearchValue(value).includes(q)
  );
}

export function filterTransactionsBySearch(
  rows: AuctionCarTransaction[],
  query: string,
  t: TFunction,
  currencyCode: string
): AuctionCarTransaction[] {
  const trimmedQuery = query.trim();
  if (!trimmedQuery) return rows;

  return rows.filter((row) => transactionMatchesSearch(row, trimmedQuery, t, currencyCode));
}
